import { useEffect, useState } from "react";

import { Container, Offcanvas } from "react-bootstrap";
import { useSelector } from "react-redux";

import SntCloseIcon from "@icons/SntCloseIcon";

import SidebarBody from "./SidebarBody";
import SidebarLogo from "./SidebarLogo";

import CloudWhiteIcon from "@images/cloud-logo-white.svg";
import CloudBlueIcon from "@images/cloud-icon-blue-01-32x32.png";

import { RootState } from "@/types/RootState";
import { LanguageKey } from "@/types/generated/LanguageKey";
import { translate } from "@/hooks/useLanguage";

function Sidebar(): JSX.Element {
  const [showMenu, setShowMenu] = useState<boolean>(false);
  const { logoPath, menuType, showCanvasMenu } = useSelector(
    (state: RootState) => state.all
  );
  const { isMobile } = useSelector((state: RootState) => state.all);
  const loginInfo = useSelector((state: RootState) => state.user);

  useEffect(() => {
    setShowMenu(showCanvasMenu ?? false);
  }, [showCanvasMenu]);

  // only close when click on link of sidebar
  const onOffCanvasClicked = (e: React.MouseEvent<HTMLDivElement>): void => {
    e.preventDefault();
    if (
      (e.target as HTMLElement).classList.contains("nav-link") ||
      (e.target as HTMLElement).classList.contains("sidebar-normal")
    ) {
      if (showMenu) {
        setShowMenu(false);
      }
    }
  };

  // Every login gets the 6.0.3 menu branding: a full-height coloured sidebar
  // with its logo at the top, deliberately covering the header's left edge. One
  // place for the mark, on a background chosen for it — the Sensolus wordmark by
  // default, the organisation's own upload where branding is configured.
  //
  // `sidebar-with-logo` no longer distinguishes anything (System and partners
  // were excluded for a while under STIC-15365); it stays because the SCSS uses
  // the compound selector to outrank the base `.sidebar` rules.
  return !isMobile ? (
    <Container>
      <div
        className="sidebar snt-sidebar sidebar-with-logo"
        data-color={menuType === "MAIN" ? "home" : "admin"}
      >
        <SidebarLogo logoPath={logoPath} />
        <SidebarBody />
      </div>
    </Container>
  ) : (
    <Container>
      <Offcanvas
        show={showMenu}
        onHide={() => {
          setShowMenu(false);
        }}
        enforceFocus={false}
        // snt-offcanvas-menu re-lays the menu out as a sheet — see sidebar.scss.
        // data-color is the menu's own theme switch: the sheet reads its colours
        // from the same variables the menu paints itself with, so an
        // organisation's branding reaches the strip too.
        className="snt-offcanvas-menu"
        data-color={menuType === "MAIN" ? "home" : "admin"}
        style={{ width: "100%" }}
        collapseonselect={"true"}
      >
        {/* A row of the sheet, not an overlay floating on top of it. What
            follows is the desktop rail's markup, and that rail is a fixed,
            250px, z-index 1010 column: anything laid over the sheet is painted
            underneath it, which is how this strip's close button became
            unreachable (STIC-16221). In flow it can't be covered, so the close
            keeps its corner and the icon keeps the middle. */}
        <div className="snt-offcanvas-menu-header">
          {loginInfo.custumInfo === undefined && (
            <img
              src={menuType === "MAIN" ? CloudWhiteIcon : CloudBlueIcon}
              style={{ width: 32, height: 32 }}
              alt={"Logo"}
            />
          )}
          <SntCloseIcon
            size={20}
            className="snt-offcanvas-menu-close"
            title={translate(LanguageKey.close_key)}
            onClick={() => setShowMenu(false)}
          />
        </div>
        <Offcanvas.Body className="p-0" onClick={onOffCanvasClicked}>
          <div
            className="sidebar snt-sidebar sidebar-with-logo"
            data-color={menuType === "MAIN" ? "home" : "admin"}
          >
            <SidebarLogo logoPath={logoPath} />
            <SidebarBody />
          </div>
        </Offcanvas.Body>
      </Offcanvas>
    </Container>
  );
}

export default Sidebar;
